import { ApiWorkflowBlockTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowBlockEdge } from "@/types/workflows/workflow.types.ts";

export interface WorkflowBlockNode<TBlock = ApiWorkflowBlock, TEdge = ApiWorkflowBlockEdge> {
  block: TBlock;
  children: WorkflowBlockNode<TBlock, TEdge>[];
  level: number;
  incomingEdges: TEdge[];
  hasMultipleParents: boolean;
}

export class WorkflowTreeBuilder<
  TBlock extends { id: number; block_type: ApiWorkflowBlockTypes } = ApiWorkflowBlock,
  TEdge extends { from_block_id: number; to_block_id: number; priority: number } =
    ApiWorkflowBlockEdge,
> {
  private blocks: Map<number, TBlock>;
  private edges: TEdge[];
  private adjacencyList: Map<number, TEdge[]>;
  private incomingEdgesMap: Map<number, TEdge[]>;

  constructor(blocks: TBlock[], edges: TEdge[]) {
    this.blocks = new Map(blocks.map((b) => [b.id, b]));
    this.edges = edges;
    this.adjacencyList = new Map();
    this.incomingEdgesMap = new Map();

    this.buildAdjacencyList();
  }

  private buildAdjacencyList() {
    this.blocks.forEach((_, id) => {
      this.adjacencyList.set(id, []);
      this.incomingEdgesMap.set(id, []);
    });

    this.edges.forEach((edge) => {
      const outgoing = this.adjacencyList.get(edge.from_block_id) || [];
      outgoing.push(edge);
      this.adjacencyList.set(edge.from_block_id, outgoing);

      const incoming = this.incomingEdgesMap.get(edge.to_block_id) || [];
      incoming.push(edge);
      this.incomingEdgesMap.set(edge.to_block_id, incoming);
    });

    this.adjacencyList.forEach((edges) => {
      edges.sort((a, b) => a.priority - b.priority);
    });
  }

  private findStartBlock(): TBlock | null {
    const startBlock = Array.from(this.blocks.values()).find(
      (b) => b.block_type === ApiWorkflowBlockTypes.start
    );

    if (startBlock) return startBlock;

    for (const [blockId, block] of this.blocks) {
      const incoming = this.incomingEdgesMap.get(blockId) || [];
      if (incoming.length === 0) {
        return block;
      }
    }

    return null;
  }

  buildTree(): WorkflowBlockNode<TBlock, TEdge> | null {
    const startBlock = this.findStartBlock();
    if (!startBlock) return null;

    const visited = new Set<number>();
    const nodesByBlockId = new Map<number, WorkflowBlockNode<TBlock, TEdge>[]>();

    const buildNode = (
      block: TBlock,
      level: number,
      incomingEdge?: TEdge
    ): WorkflowBlockNode<TBlock, TEdge> => {
      const allIncomingEdges = this.incomingEdgesMap.get(block.id) || [];
      const hasMultipleParents = allIncomingEdges.length > 1;

      const node: WorkflowBlockNode<TBlock, TEdge> = {
        block,
        children: [],
        level,
        incomingEdges: incomingEdge ? [incomingEdge] : [],
        hasMultipleParents,
      };

      if (!nodesByBlockId.has(block.id)) {
        nodesByBlockId.set(block.id, []);
      }
      nodesByBlockId.get(block.id)!.push(node);

      if (visited.has(block.id)) {
        return node;
      }

      visited.add(block.id);

      const outgoingEdges = this.adjacencyList.get(block.id) || [];

      outgoingEdges.forEach((edge) => {
        const childBlock = this.blocks.get(edge.to_block_id);
        if (childBlock) {
          const childNode = buildNode(childBlock, level + 1, edge);
          node.children.push(childNode);
        }
      });

      return node;
    };

    return buildNode(startBlock, 0);
  }
}
